/**
 * The common viewers' model (brand/crow-extensions.html, "Visionneuses communes"): which viewer
 * reads a file, by its media type, and the Markdown edits a viewer makes itself (a task ticked).
 */

const MEDIA_TYPES: Record<string, string> = {
  png: 'image/png',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
  avif: 'image/avif',
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
  webm: 'video/webm',
  mp3: 'audio/mpeg',
  wav: 'audio/wav',
  ogg: 'audio/ogg',
  m4a: 'audio/mp4',
  flac: 'audio/flac',
  pdf: 'application/pdf',
  md: 'text/markdown',
  markdown: 'text/markdown',
  csv: 'text/csv',
  tsv: 'text/tab-separated-values',
  html: 'text/html',
  htm: 'text/html',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  pptx: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
};

/** The media type of a file, from its extension. */
export function mediaTypeOf(path: string): string | undefined {
  return MEDIA_TYPES[path.split('.').at(-1)?.toLowerCase() ?? ''];
}

/**
 * The viewer that reads a file: the five common ones (Markdown, image, PDF, video, table), then
 * what the core still shows itself (audio, a web page in a sandbox, text), else a download.
 */
export type ViewerKind =
  | 'markdown'
  | 'image'
  | 'pdf'
  | 'video'
  | 'table'
  | 'audio'
  | 'html'
  | 'text'
  | 'download';

export function viewerOf(path: string): ViewerKind {
  const type = mediaTypeOf(path);
  if (type === undefined) return 'text';
  if (type === 'text/markdown') return 'markdown';
  if (type.startsWith('image/')) return 'image';
  if (type === 'application/pdf') return 'pdf';
  // QuickTime is not played by every browser, but the video element tries.
  if (type.startsWith('video/')) return 'video';
  if (
    type === 'text/csv' ||
    type === 'text/tab-separated-values' ||
    type.endsWith('spreadsheetml.sheet')
  )
    return 'table';
  if (type.startsWith('audio/')) return 'audio';
  if (type === 'text/html') return 'html';
  return 'download';
}

/** Whether the host saves a file's edits itself: Markdown, text the viewer can write back. */
export const editableInViewer = (path: string) => viewerOf(path) === 'markdown';

/** A Markdown file split into its front matter (kept as is) and its body (shown). */
export function splitFrontMatter(source: string): { frontMatter: string; body: string } {
  const match = /^---\r?\n[\s\S]*?\r?\n---(?:\r?\n)+/.exec(source);
  return match === null
    ? { frontMatter: '', body: source }
    : { frontMatter: match[0], body: source.slice(match[0].length) };
}

const TASK = /^(\s*(?:[-*+]|\d+[.)])\s+\[)([ xX])(\])/;
const FENCE = /^\s*(`{3,}|~{3,})/;

/**
 * The lines of a Markdown text that are tasks (`- [ ]`, `1. [x]`), in order, outside code blocks:
 * the order a renderer draws their checkboxes in.
 */
export function taskLines(source: string): { line: number; done: boolean }[] {
  const tasks: { line: number; done: boolean }[] = [];
  let fence: string | null = null;
  source.split('\n').forEach((text, line) => {
    const opened = FENCE.exec(text)?.[1];
    if (opened !== undefined) {
      if (fence === null) fence = opened[0] ?? null;
      else if (opened.startsWith(fence)) fence = null;
      return;
    }
    if (fence !== null) return;
    const task = TASK.exec(text);
    if (task !== null) tasks.push({ line, done: task[2] !== ' ' });
  });
  return tasks;
}

/** The text with its `index`-th task ticked (or unticked): its line only changes. */
export function setTask(source: string, index: number, done: boolean): string {
  const task = taskLines(source)[index];
  if (task === undefined) return source;
  const lines = source.split('\n');
  lines[task.line] = (lines[task.line] ?? '').replace(TASK, `$1${done ? 'x' : ' '}$3`);
  return lines.join('\n');
}

/** The separator of a CSV file: the one splitting its first lines into the most, equal, columns. */
export function csvSeparator(source: string): ',' | ';' | '\t' {
  const lines = source
    .split(/\r?\n/)
    .filter((line) => line.length > 0)
    .slice(0, 10);
  let best: ',' | ';' | '\t' = ',';
  let bestScore = 0;
  for (const separator of [',', ';', '\t'] as const) {
    const counts = lines.map((line) => line.split(separator).length);
    const columns = Math.min(...counts);
    const score =
      columns > 1 ? columns * (counts.every((count) => count === counts[0]) ? 2 : 1) : 0;
    if (score > bestScore) {
      best = separator;
      bestScore = score;
    }
  }
  return best;
}
